import React, { useEffect } from 'react';
import {
  Box,
  BoxProps,
  FormControl,
  FormControlProps,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
} from '@mui/material';

import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useFlag } from '@unleash/proxy-client-react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { TP_COLORS } from '../../constants';
import { getSubjectColor, isMobileScreen, SafeAreaGaps } from '../../helper';
import { headerHeight } from '../Mobile/Header';
import { useAppSelector } from '../../hooks';
import { getSubTopicById, getSubTopicState } from '../../store/SubTopicSlice';
import { ISessionUser } from '../../pages/UserPages/Resources';
import { getSessionState, selectStudent } from '../../store/SessionSlice';
import { getStudentProgress } from '../../store/ProgressSlice';
import { getAuthState } from '../../store/AuthSlice';

export interface ITopicHeader {
  boxProps?: BoxProps;
  subject: string;
  topic: string;
  subTopics: Array<string>;
  relatedStudents?: Array<ISessionUser>;
  onChangeStudent?: (studentId: string) => void;
  isGetSubTopic?: boolean;
}

const titleTextProps: TypographyProps = {
  variant: 'h2',
  style: {
    color: TP_COLORS.white,
    fontWeight: 500,
  },
  sx: {
    fontSize: {
      xs: '16px',
      sm: '20px',
      md: '26px',
      lg: '28px',
      xl: '30px',
    },
  },
};

const subTitleTextProps: TypographyProps = {
  variant: 'h1',
  style: {
    color: TP_COLORS.white,
    fontWeight: 500,
    opacity: 0.6,
    marginTop: '10px',
  },
  sx: {
    fontSize: {
      xs: '18px',
      sm: '22px',
      md: '28px',
      lg: '30px',
      xl: '32px',
    },
  },
};

const statusTextProps: TypographyProps = {
  variant: 'subtitle1',
  style: {
    color: TP_COLORS.white,
  },
};

const selectProps: FormControlProps = {
  variant: 'standard',
  sx: {
    paddingTop: '2px',
    paddingLeft: '5px',
    color: TP_COLORS.white,
  },
  size: 'small',
};

const headerContentStyles: React.CSSProperties = {
  color: TP_COLORS.white,
  msTextOverflow: 'ellipsis',
  display: 'flex',
  alignItems: 'center',
  width: 'calc(100% - 200px)',
  maxWidth: 'calc(100% - 200px)',
};

const stackProps: StackProps = {
  direction: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
};

const TopicHeader: React.FC<ITopicHeader> = ({
  boxProps,
  subject,
  topic,
  subTopics,
  relatedStudents,
  onChangeStudent,
  isGetSubTopic = true,
}) => {
  const params = useParams();
  const dispatch = useDispatch();
  const isMobile = isMobileScreen();

  const defaultContainerStyles: BoxProps['sx'] = {
    background: getSubjectColor(subject),
    minHeight: '80px',
    display: !isMobile ? 'flex' : undefined,
    pt: {
      xs: `calc(${SafeAreaGaps.top} + ${headerHeight})`,
      sm: `calc(${SafeAreaGaps.top} + 15px)`,
    },
    pb: '15px',
    pr: '15px',
    pl: {
      xs: '15px',
      sm: '20px',
      md: '30px',
      lg: '35px',
    },
  };

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const sessionState = useAppSelector(getSessionState);
  const authState = useAppSelector(getAuthState);

  // State
  const {
    subTopic: subTopicData,
    groupedTopicsWithSubTopicsData,
  } = subTopicState;
  const urlStringPattern = params.pattern || '';
  const urlStringPatternDivided = urlStringPattern.split('_').reverse();
  const subTopicId = urlStringPatternDivided[0] || '';
  const topicData =
    subTopicData && subTopicData.topic ? subTopicData.topic : undefined;

  const { selectedStudent } = sessionState;
  const userRole = authState.user?.role.name;

  const defaultOrderEnabled = useFlag(
    'learning_platform_subtopic_default_order_enabled'
  );

  useEffect(() => {
    if (subTopicId && !subTopicData && isGetSubTopic) {
      dispatch(getSubTopicById(subTopicId));
    }
    if (subTopicId && userRole === 'Student') {
      dispatch(getStudentProgress(subTopicId));
    }
  }, []);

  useEffect(() => {
    if (relatedStudents && relatedStudents.length) {
      if (!selectedStudent && !relatedStudents[0].autoConnectDisabled) {
        handleStudentChange(relatedStudents[0]._id);
      } else if (
        !relatedStudents.find((student) => student._id === selectedStudent)
      ) {
        handleStudentChange('');
      } else {
        handleStudentChange(selectedStudent);
      }
    }
  }, [relatedStudents]);

  const handleStudentChange = (studentId: string) => {
    dispatch(selectStudent(studentId));
    if (onChangeStudent) {
      onChangeStudent(studentId);
    }
  };

  const getIndexes = (): {
    topicIndex?: number;
    subTopicIndex?: number;
  } => {
    const topicList = groupedTopicsWithSubTopicsData?.list;
    let topicIndex = -1;
    let subTopicIndex = -1;

    if (topicList && topicData) {
      topicIndex = topicList.findIndex((item) => item._id === topicData?._id);
      const subTopicList = topicList[topicIndex]?.subTopics;

      if (defaultOrderEnabled) {
        topicIndex = topicIndex >= 0 ? topicIndex + 1 : -1;
      } else {
        topicIndex = topicList[topicIndex]?.sortingOrder || 0;
      }

      if (subTopicList && subTopicId) {
        subTopicIndex = subTopicList.findIndex(
          (item) => item._id === subTopicId
        );
        if (defaultOrderEnabled) {
          subTopicIndex = subTopicIndex >= 0 ? subTopicIndex + 1 : -1;
        } else {
          subTopicIndex = Number(
            subTopicList[subTopicIndex]?.sortingOrder || 0
          );
        }
      }
    }
    return {
      topicIndex: topicIndex >= 0 ? topicIndex : undefined,
      subTopicIndex: subTopicIndex >= 0 ? subTopicIndex : undefined,
    };
  };

  const { topicIndex, subTopicIndex } = getIndexes();

  const RENDER_RIGHT_SECTION = () => (
    <>
      {/* Session View */}
      {relatedStudents ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography {...statusTextProps}>
            Connected: {relatedStudents.length === 0 ? 'No Students' : ''}
          </Typography>
          {relatedStudents.length > 0 ? (
            <FormControl {...selectProps}>
              <Select
                autoWidth
                displayEmpty
                id="student-select"
                value={selectedStudent}
                onChange={(event: SelectChangeEvent) =>
                  handleStudentChange(event.target.value)
                }
                disableUnderline
                sx={{
                  color: TP_COLORS.white,
                  '.MuiSvgIcon-root ': {
                    fill: TP_COLORS.white,
                  },
                }}
              >
                <MenuItem key="" value="">
                  Original Resources
                </MenuItem>
                {relatedStudents.map((element) => (
                  <MenuItem
                    key={element._id}
                    value={element._id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexDirection: 'row',
                    }}
                  >
                    <span>
                      {element.firstname} {element.lastname}
                    </span>
                    {element.isOnline && (
                      <span style={{ color: 'green', marginLeft: '8px' }}>
                        ●
                      </span>
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
        </div>
      ) : null}
    </>
  );

  return (
    <div>
      <Box
        component="div"
        {...boxProps}
        sx={
          boxProps && boxProps.sx
            ? { ...defaultContainerStyles, ...boxProps.sx }
            : defaultContainerStyles
        }
      >
        {isMobile ? (
          <div
            style={{
              paddingTop: isMobile
                ? `calc(${headerHeight} - 30px + ${SafeAreaGaps.top})`
                : '40px',
              textAlign: 'center',
            }}
          >
            <Typography {...titleTextProps}>
              {topic ? `${topicIndex || 0}. ` : ''}
              {topic}
            </Typography>
            {subTopics.map((subTopic, index) => (
              <Typography key={index} {...subTitleTextProps}>
                {subTopic && index === 0
                  ? `${topicIndex || 0}.${subTopicIndex || 0} `
                  : ''}
                {subTopic}
              </Typography>
            ))}

            <div style={{ minHeight: '30px', marginTop: '10px' }}>
              {RENDER_RIGHT_SECTION()}
            </div>
          </div>
        ) : (
          <Stack {...stackProps}>
            <div
              className="ellipsis-text.two-lines"
              style={headerContentStyles}
            >
              <Typography component="h2" variant="subtitle1" fontWeight="600">
                {topic ? `${topicIndex || 0}. ` : ''}
                {topic}
              </Typography>
              &nbsp;
              {topic && subTopics && <ChevronRightIcon />}
              &nbsp;
              <Typography component="h1" variant="subtitle1" fontWeight="600">
                {subTopics[0]
                  ? `${topicIndex || 0}.${subTopicIndex || 0} `
                  : ''}
                {subTopics[0]}
              </Typography>
              {subTopics[1] && (
                <>
                  &nbsp;
                  <ChevronRightIcon />
                  &nbsp;
                  <Typography
                    component="h1"
                    variant="subtitle1"
                    fontWeight="600"
                  >
                    {subTopics[1]}
                  </Typography>
                </>
              )}
            </div>

            {RENDER_RIGHT_SECTION()}
          </Stack>
        )}
      </Box>
    </div>
  );
};

export default TopicHeader;
